/**
 * Lazy-loaded Stripe SDK singleton.
 *
 * Stripe.js is loaded on first call and cached for the rest of the
 * session — every component that needs the SDK (elements form, payment
 * wrapper, resume flow) calls this composable rather than reimplementing
 * its own script-tag-injection.
 *
 * The cache lives at module scope so a re-mount of the consumer doesn't
 * re-fetch the script. SSR is a no-op — the SDK only exists in the browser.
 */
type StripeSDK = {
  elements: (...args: unknown[]) => unknown;
  confirmPayment?: (...args: unknown[]) => Promise<unknown>;
  retrievePaymentIntent: (
    clientSecret: string,
  ) => Promise<{ paymentIntent?: { id: string; status: string; [key: string]: unknown } | null }>;
  [key: string]: unknown;
};

let cachedStripe: StripeSDK | null = null;
let inflightLoad: Promise<StripeSDK | null> | null = null;

const STRIPE_SCRIPT_SRC = "https://js.stripe.com/v3/";

export function useStripe() {
  const config = useRuntimeConfig();

  /** Resolve the Stripe SDK, loading the script tag on first call. */
  function getStripe(): Promise<StripeSDK | null> {
    if (!import.meta.client) return Promise.resolve(null);
    if (cachedStripe) return Promise.resolve(cachedStripe);
    if (inflightLoad) return inflightLoad;

    const publishableKey = config.public.stripeKey as string | undefined;
    if (!publishableKey) {
      // Mis-configured deploy — surface in dev, gracefully no-op in prod
      // so the rest of the app stays renderable.
      // eslint-disable-next-line no-console
      if (import.meta.dev) console.error("NUXT_PUBLIC_STRIPE_KEY is not set");
      return Promise.resolve(null);
    }

    inflightLoad = new Promise<StripeSDK | null>((resolve) => {
      const finalize = () => {
        const ctor = (window as unknown as { Stripe?: (key: string) => StripeSDK }).Stripe;
        if (!ctor) {
          inflightLoad = null;
          resolve(null);
          return;
        }
        cachedStripe = ctor(publishableKey);
        inflightLoad = null;
        resolve(cachedStripe);
      };

      const existing = document.querySelector(`script[src="${STRIPE_SCRIPT_SRC}"]`);
      if (existing) {
        // Script tag exists but the global may not yet be assigned — wait
        // for it to load before resolving.
        if ((window as unknown as { Stripe?: unknown }).Stripe) {
          finalize();
        } else {
          existing.addEventListener("load", finalize, { once: true });
        }
        return;
      }

      const script = document.createElement("script");
      script.src = STRIPE_SCRIPT_SRC;
      script.async = true;
      script.onload = finalize;
      document.head.appendChild(script);
    });

    return inflightLoad;
  }

  /**
   * Fetch the live Stripe PaymentIntent status for a given client_secret.
   * Returns null when the SDK isn't available (SSR, missing key) or the
   * call fails — callers default to a safe "still finalising" path rather
   * than prompting for new card details.
   */
  async function fetchStripePaymentIntentStatus(clientSecret: string): Promise<string | null> {
    if (!clientSecret) return null;
    const stripe = await getStripe();
    if (!stripe) return null;
    try {
      const { paymentIntent } = await stripe.retrievePaymentIntent(clientSecret);
      return paymentIntent?.status ?? null;
    } catch {
      return null;
    }
  }

  return { getStripe, fetchStripePaymentIntentStatus };
}
